import * as yup from 'yup';

import { UuidableName } from 'utils/api/types';
import { ChoiceValue } from 'utils/library';

import { CONTROL_FIELD, ControlStatus } from '../../constants';

export type CreateControlValues = {
  name: string;
  description: string;
  category: ChoiceValue;
  owner: UuidableName | null;
  status: ChoiceValue;
  dueDate: number | null;
};

const choiceRequired = (message: string) =>
  yup
    .mixed<NonNullable<ChoiceValue>>()
    .nullable()
    .test('chosen', message, (value) => Array.isArray(value) && value.length > 0)
    .required(message);

export const createControlSchema = yup.object({
  name: yup.string().trim().required('Enter a name'),
  description: yup.string().trim().required('Enter a description'),
  category: choiceRequired('Choose a category'),
  owner: yup.mixed<UuidableName>().nullable().required('Choose an owner'),
  status: choiceRequired('Choose a status'),
  dueDate: yup
    .number()
    .nullable()
    .when('status', {
      is: (status: ChoiceValue) => status?.[0]?.key === ControlStatus.Proposed,
      then: (schema) => schema.required('Choose a due date'),
    }),
});

export const toFieldValues = (values: CreateControlValues): Record<string, string | number | string[] | null> => {
  const proposed = values.status?.[0]?.key === ControlStatus.Proposed;

  return {
    [CONTROL_FIELD.description]: values.description.trim(),
    [CONTROL_FIELD.category]: values.category?.map(({ key }) => key) ?? null,
    [CONTROL_FIELD.owner]: values.owner?.uuid ?? null,
    [CONTROL_FIELD.status]: values.status?.map(({ key }) => key) ?? null,
    ...(proposed ? { [CONTROL_FIELD.dueDate]: values.dueDate } : {}),
  };
};

export const withDefaultDueTime = (previous: number | null, next: number | null, hour: number): number | null => {
  if (previous !== null || next === null) {
    return next;
  }
  const picked = new Date(next);
  picked.setHours(hour, 0, 0, 0);
  return picked.getTime();
};
